import React, { useMemo, useState } from 'react';

import { Stack } from '@mui/material';

import type { CmChecklist } from '@luxuryescapes/contract-svc-offer';

import ChecklistActiveTab from './ChecklistActiveTab';
import ChecklistToolbar from './ChecklistToolbar';
import ContextCard from './ContextCard';
import { isRowApproved } from './approvalQueueModel';
import { SETUP_CATEGORY, getChecklistCategory } from './checklistCategories';
import { getAllChecklistRows } from './checklistRows';
import { presentChecklist } from './presenters/presentChecklist';
import type { ChecklistRowsByCategory, ChecklistTabId } from './types';
import { useActiveCategoryRows } from './useActiveCategoryRows';
import { useApprovalQueueModel } from './useApprovalQueueModel';
import { useChecklistApprovalState } from './useChecklistApprovalState';
import { useChecklistSummary } from './useChecklistSummary';
import { useReconcileStaleDecisions } from './useReconcileStaleDecisions';
import { useSearchMatchCounts } from './useSearchMatchCounts';
import { useVisibleChecklistRows } from './useVisibleChecklistRows';

export interface ChecklistContext {
  bookByDate?: string;
  campaignType?: string;
  channelManager?: string;
  hotelName: string;
  offerId: string;
  propertyId?: string;
  salesforceOpportunityId?: string;
  travelToDate?: string;
  vendorId?: string;
}

interface Props extends ChecklistContext {
  evaluation: CmChecklist.Evaluation;
}

// Rendered only once the evaluation has loaded, so every row-derived hook has rows to work with.
export default function LoadedChecklistWorkspace({
  bookByDate,
  campaignType,
  channelManager,
  evaluation,
  hotelName,
  offerId,
  propertyId,
  salesforceOpportunityId,
  travelToDate,
  vendorId,
}: Props) {
  const [showAutoApproved, setShowAutoApproved] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTabId, setActiveTabId] = useState<ChecklistTabId>('approvalQueue');

  const presented = useMemo(() => presentChecklist(evaluation), [evaluation]);

  useReconcileStaleDecisions({ evaluation, offerId });

  const checklistRows = useMemo<ChecklistRowsByCategory>(
    () => ({
      baseSurchargesRows: presented.baseSurchargesRows,
      blackoutRows: presented.blackoutRows,
      bundledExperienceRows: presented.bundledExperienceRows,
      campaignRows: presented.campaignRows,
      capacityRows: presented.capacityRows,
      discountsMarginRows: presented.discountsMarginRows,
      roomRows: presented.roomRows,
      dealOptionApprovalRows: presented.dealOptionApprovalRows,
      dealOptionsInclusionsRows: presented.dealOptionsInclusionsRows,
      egsAgeCoverageRows: presented.egsAgeCoverageRows,
      marginRows: presented.marginRows,
      setupRows: presented.setupRows,
      surchargeTableRows: presented.surchargeTableRows,
      taxesPaymentsRows: presented.taxesPaymentsRows,
    }),
    [presented],
  );
  const allChecklistRows = useMemo(() => getAllChecklistRows(checklistRows), [checklistRows]);

  const {
    approveRow,
    expandedQueueItems,
    expandVisibleQueueItems,
    manualOverrides,
    markRowsApproved,
    toggleQueueItemDetails,
    unapproveRow,
  } = useChecklistApprovalState({ offerId, rows: allChecklistRows });

  const isApprovalQueueMode = activeTabId === 'approvalQueue';
  const activeCategory = isApprovalQueueMode ? SETUP_CATEGORY : getChecklistCategory(activeTabId);
  // A category can be degraded by any of its source_ids failing, not just the opportunity — the
  // evaluation already tracks this per category, so read it rather than special-casing one source.
  const isActiveCategoryDegraded =
    evaluation.categories.find((category) => category.id === activeCategory.id)?.degraded ?? false;

  const visibleRows = useVisibleChecklistRows({
    activeCategoryId: activeCategory.id,
    isApprovalQueueMode,
    manualOverrides,
    searchQuery,
    showAutoApproved,
    ...checklistRows,
  });
  const { reportSummaries, visibleQueueItems } = useApprovalQueueModel({
    blackoutCoverage: presented.blackoutCoverage,
    manualOverrides,
    offerId,
    propertyId,
    rows: checklistRows,
    vendorId,
    visibleRows,
  });
  const { activeExpandableItems, activeTabRows } = useActiveCategoryRows({
    activeCategoryId: activeCategory.id,
    dealOptionsInclusionsDealRows: presented.dealOptionsInclusionsDealRows,
    isApprovalQueueMode,
    visibleQueueItems,
    visibleRows,
    ...checklistRows,
  });
  const areActiveTabDetailsExpanded =
    activeExpandableItems.length > 0 &&
    activeExpandableItems.every((item) => expandedQueueItems[item.id] ?? item.defaultOpen ?? false);
  const summary = useChecklistSummary({ manualOverrides, ...checklistRows });
  const searchMatchCounts = useSearchMatchCounts(checklistRows, searchQuery);

  return (
    <Stack spacing={2}>
      <ContextCard
        approvedCount={summary.approvedCount}
        bookByDate={bookByDate}
        campaignType={campaignType}
        channelManager={channelManager}
        hotelName={hotelName}
        needsApprovalCount={summary.needsApprovalCount}
        onSelectApprovalQueue={() => setActiveTabId('approvalQueue')}
        offerId={offerId}
        salesforceOpportunityId={salesforceOpportunityId}
        travelToDate={travelToDate}
        totalCount={summary.totalCount}
      />

      <ChecklistToolbar
        activeTabId={activeTabId}
        areVisibleQueueItemsExpanded={areActiveTabDetailsExpanded}
        categoryCounts={summary.categoryCounts}
        isApprovalQueueMode={isApprovalQueueMode}
        needsApprovalCount={summary.needsApprovalCount}
        searchMatchCounts={searchMatchCounts}
        searchQuery={searchQuery}
        showAutoApproved={showAutoApproved}
        visibleQueueItemCount={activeExpandableItems.length}
        onExpandVisibleCards={() => expandVisibleQueueItems(activeExpandableItems)}
        onMarkAllApproved={!isApprovalQueueMode ? () => markRowsApproved(activeTabRows) : undefined}
        onMarkAllApprovedDisabled={activeTabRows
          .filter((row) => row.status !== 'blocked')
          .every((row) => isRowApproved(row, manualOverrides))}
        onSearchQueryChange={setSearchQuery}
        onShowAutoApprovedChange={() => setShowAutoApproved((current) => !current)}
        onTabChange={setActiveTabId}
        syncedAt={!isApprovalQueueMode ? presented.categorySyncedAt[activeCategory.id] : undefined}
      />

      <ChecklistActiveTab
        activeCategory={activeCategory}
        blackoutCoverage={presented.blackoutCoverage}
        dealOptionsInclusionsDealRows={presented.dealOptionsInclusionsDealRows}
        expandedQueueItems={expandedQueueItems}
        isApprovalQueueMode={isApprovalQueueMode}
        manualOverrides={manualOverrides}
        needsApprovalCount={summary.needsApprovalCount}
        isCategoryDegraded={isActiveCategoryDegraded}
        reportSummaries={reportSummaries}
        rows={checklistRows}
        searchQuery={searchQuery}
        showAutoApproved={showAutoApproved}
        travelFrom={presented.opportunityTravelFrom}
        travelTo={presented.opportunityTravelTo}
        unlinkedDealOptions={presented.unlinkedDealOptions}
        visibleQueueItems={visibleQueueItems}
        visibleRows={visibleRows}
        onApprovalChange={approveRow}
        onSelectTab={setActiveTabId}
        onToggleDetails={toggleQueueItemDetails}
        onUnapprovalChange={unapproveRow}
      />
    </Stack>
  );
}
